/**
 * Client-side layout generator — mirrors the Python generate_deterministic_layout()
 * in backend/server.py so the wizard can show a live SVG preview without any API call.
 */

const round = (v, decimals) => Math.round(v * 10 ** decimals) / 10 ** decimals;

/**
 * @param {object} inputs
 * @param {number} inputs.land_length   — already in metres
 * @param {number} inputs.land_width    — already in metres
 * @param {string} inputs.location_type
 * @param {Array}  inputs.features       — ['cafe','store','car_wash','restrooms']
 * @param {Array}  inputs.charger_config — [{type:'60kW_DC', quantity:4}, ...]
 * @param {boolean} inputs.solar_required
 * @returns layout object (same shape as backend) or null when dimensions are too small
 */
export function generateLiveLayout(inputs) {
  const landLength = parseFloat(inputs.land_length) || 0;
  const landWidth  = parseFloat(inputs.land_width)  || 0;
  if (landLength < 10 || landWidth < 10) return null;

  const margin       = 3;
  const usableLength = landLength - 2 * margin;
  const usableWidth  = landWidth  - 2 * margin;

  // ── Charger configuration ────────────────────────────────────────────────
  // Each configured "charger" is a dual-gun unit → 2 parking bays per unit.
  const chargerConfig = inputs.charger_config || [{ type: "60kW_DC", quantity: 2 }];
  const totalUnits    = chargerConfig.reduce((s, c) => s + (parseInt(c.quantity) || 0), 0);

  const chargerBayWidth  = 3.5;   // width of ONE bay
  const chargerBayLength = 6;
  const pairGap          = 0.4;   // gap between the 2 bays of one unit (for pedestal)
  const pairExternalGap  = 1.5;   // gap between different charger units
  const pairWidth        = 2 * chargerBayWidth + pairGap; // 7.4m per unit

  const unitsPerRow = Math.max(1, Math.floor(usableLength / (pairWidth + pairExternalGap)));
  const maxRows     = usableWidth > 20 ? 2 : 1;
  const maxUnits    = unitsPerRow * maxRows;
  const numUnits    = Math.max(0, Math.min(totalUnits || 2, maxUnits));
  const numChargers = numUnits * 2; // total bays shown

  const chargerStartY    = margin + 2;
  const chargerPositions = [];
  let unitsPlaced        = 0;
  let row                = 0;

  while (unitsPlaced < numUnits && row < maxRows) {
    const unitsInRow = Math.min(numUnits - unitsPlaced, unitsPerRow);
    const rowTotalW  = unitsInRow * pairWidth + (unitsInRow - 1) * pairExternalGap;
    const rowStartX  = margin + (usableLength - rowTotalW) / 2;

    for (let i = 0; i < unitsInRow; i++) {
      const pairStartX = rowStartX + i * (pairWidth + pairExternalGap);
      const y          = chargerStartY + row * (chargerBayLength + 3);
      const unitIdx    = unitsPlaced + i;
      const unitType   = _getUnitType(chargerConfig, unitIdx);
      const unitPower  = _inferPowerKw(chargerConfig, unitIdx);
      const bayIdBase  = unitIdx * 2;

      // Left bay of unit
      chargerPositions.push({
        id:         `C${bayIdBase + 1}`,
        x:          round(pairStartX + chargerBayWidth / 2, 1),
        y:          round(landWidth - y - chargerBayLength, 1),
        type:       unitType,
        bay_width:  chargerBayWidth,
        bay_length: chargerBayLength,
        power_kw:   unitPower,
        pair_index: unitIdx,
        pair_side:  "left",
      });
      // Right bay of unit
      chargerPositions.push({
        id:         `C${bayIdBase + 2}`,
        x:          round(pairStartX + chargerBayWidth + pairGap + chargerBayWidth / 2, 1),
        y:          round(landWidth - y - chargerBayLength, 1),
        type:       unitType,
        bay_width:  chargerBayWidth,
        bay_length: chargerBayLength,
        power_kw:   unitPower,
        pair_index: unitIdx,
        pair_side:  "right",
      });
    }
    unitsPlaced += unitsInRow;
    row++;
  }

  // ── Parking (overflow only — charger bays above already serve as EV parking) ──
  const parkingBayWidth  = 2.5;
  const parkingBayLength = 5;
  const parkingStartY    = chargerStartY + maxRows * (chargerBayLength + 3) + 2;
  const parkingPerRow    = Math.max(1, Math.floor(usableLength / (parkingBayWidth + 0.5)));
  const numParking       = Math.min(parkingPerRow, 2); // 2 overflow/waiting spots

  const parkingPositions = [];
  const parkingStartX    = margin + (usableLength - numParking * (parkingBayWidth + 0.5)) / 2;
  for (let i = 0; i < numParking; i++) {
    const x = parkingStartX + i * (parkingBayWidth + 0.5);
    parkingPositions.push({
      id:     `P${i + 1}`,
      x:      round(x, 1),
      y:      round(landWidth - parkingStartY - parkingBayLength, 1),
      width:  parkingBayWidth,
      height: parkingBayLength,
    });
  }

  // ── Amenity buildings ─────────────────────────────────────────────────────
  const buildings = {};
  const features  = inputs.features || [];
  let buildingX   = landLength - margin - 12;
  let buildingY   = margin + 5;

  if (features.includes("cafe")) {
    buildings.cafe = { x: round(buildingX, 1), y: round(landWidth - buildingY - 10, 1), width: 10, height: 8 };
    buildingY += 12;
  }
  if (features.includes("store")) {
    buildings.store = { x: round(buildingX, 1), y: round(landWidth - buildingY - 8, 1), width: 8, height: 6 };
    buildingY += 10;
  }
  if (features.includes("restrooms")) {
    buildings.restrooms = { x: round(buildingX, 1), y: round(landWidth - buildingY - 6, 1), width: 6, height: 4 };
    buildingY += 8;
  }
  if (features.includes("car_wash")) {
    buildings.car_wash = { x: round(buildingX - 12, 1), y: round(margin + 2, 1), width: 10, height: 8 };
  }

  // ── Solar canopy ──────────────────────────────────────────────────────────
  // Min site size for solar: 10×10m. Min canopy: 4m × 3m.
  const solarViable = landLength >= 10 && landWidth >= 10;
  let solarArea = 0, solarKwp = 0, solarPosition = null;
  if (inputs.solar_required !== false && solarViable) {
    const solarWidth  = Math.max(4, Math.min(usableLength * 0.7, usableLength - 2));
    const solarHeight = Math.max(3, Math.min(
      usableWidth * 0.4,
      parkingStartY + parkingBayLength - chargerStartY + 4,
    ));
    // Only place if canopy fits within usable area
    if (solarWidth <= usableLength && solarHeight <= usableWidth) {
      const solarX = margin + (usableLength - solarWidth) / 2;
      const solarY = chargerStartY - 2;
      solarArea    = solarWidth * solarHeight;
      solarKwp     = round(solarArea * 0.15, 1);
      solarPosition = {
        x:      round(solarX, 1),
        y:      round(landWidth - solarY - solarHeight, 1),
        width:  round(solarWidth, 1),
        height: round(solarHeight, 1),
      };
    }
  }

  // ── Traffic flow ──────────────────────────────────────────────────────────
  const trafficFlow = {
    entry:       { x: 0, y: round(landWidth / 2, 1), direction: "east" },
    exit:        { x: round(landLength, 1), y: round(landWidth / 2 - 5, 1), direction: "east" },
    circulation: "One-way flow: Enter from left, charge/park in centre, exit to right",
  };

  const loc = (inputs.location_type || "urban");
  const locLabel = loc.charAt(0).toUpperCase() + loc.slice(1);

  return {
    template_used: `${numUnits}-unit ${locLabel} Station`,
    charger_arrangement: { count: numChargers, positions: chargerPositions, unit_count: numUnits },
    parking:            { count: numParking,   positions: parkingPositions },
    buildings,
    traffic_flow: trafficFlow,
    solar: {
      area_sqm:      round(solarArea, 1),
      estimated_kWp: solarKwp,
      position:      solarPosition,
    },
    dimensions: { total_length: landLength, total_width: landWidth },
    notes: `Live preview — ${landLength}m × ${landWidth}m · ${numUnits} charger units (${numChargers} bays) · ${numParking} overflow parking`,
  };
}

/** Return power_kw for charger unit at unitIndex */
function _inferPowerKw(chargerConfig, unitIndex) {
  const POWER_MAP = {
    "30kW_DC":  30,  "60kW_DC":  60,  "120kW_DC": 120,
    "150kW_DC": 150, "22kW_AC":  22,
  };
  let offset = 0;
  for (const cfg of chargerConfig) {
    const qty = parseInt(cfg.quantity) || 0;
    if (unitIndex < offset + qty) return POWER_MAP[cfg.type] || null;
    offset += qty;
  }
  return null;
}

/** Return AC_Slow or DC_Fast type string for charger unit at unitIndex */
function _getUnitType(chargerConfig, unitIndex) {
  let offset = 0;
  for (const cfg of chargerConfig) {
    const qty = parseInt(cfg.quantity) || 0;
    if (unitIndex < offset + qty)
      return (cfg.type || "").toLowerCase().includes("ac") ? "AC_Slow" : "DC_Fast";
    offset += qty;
  }
  return "DC_Fast";
}
